import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';

import { HisUiService } from '../../core/service/base/his-ui.service';
import { HisNotificationService } from '../../core/service/notifications/his-notification.service';
import { HisOption } from '../../genericsearch/model/his-option';
import { HisMultiSelectWidget } from '../../genericsearch/model/his-widget-multiselect';

import { AutoComplete, AutoCompleteSelectEvent } from 'primeng/autocomplete';

@Component( {
    selector: 'his-search-multiselect',
    templateUrl: './his-search-multiselect.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
} )
export class HisSearchMultiselectComponent implements OnInit, AfterViewInit {
    private notificationService = inject( HisNotificationService );
    private uiService = inject( HisUiService );


    // widget is only passed to let the plam extract attributes like helptext from the search config file;
    // it will be undefined when the component is used standalone, outside the generic search.
    @Input() widget: HisMultiSelectWidget;

    // unique id for html
    @Input() uid: string;

    // The selection
    @Input() selectedOptions: HisOption[] = [];

    // minimum length of autocomplete input to trigger a search
    @Input() minLength = 1;
    // maximum number of suggestions to show in autocomplete (null -> show all)
    @Input() limit: number = null; // XXX currently unused
    // show icon to open dropdown?
    @Input() showDropdownIcon = true;
    // show null item in autocomplete select list?
    @Input() showNullItem = false;

    // true if this is the first input element in the search mask
    @Input() isFirstInputElement = false;
    // the input element
    @ViewChild( 'inputElement' ) inputElement: AutoComplete;

    // all selectable options
    options: HisOption[] = [];
    // suggestions for autocomplete
    filteredSelectList: HisOption[] = [];
    // previous autocomplete input string
    lastAutocompleteQuery: string = undefined;

    @Output() changed: EventEmitter<HisOption[]> = new EventEmitter();

    @Input() showUnequalOption = false;

    ngOnInit() {
        this.widget.options.subscribe( ( values: HisOption[] ) => {
            this.options = [];
            // avoid that the null item is added twice
            if ( values ) {

                if ( this.widget.showNullItem || this.showNullItem ) {
                    // (nicht gefüllt)/(value not existing) should be the second option of the options list, see #325731
                    let nullValueLabel = this.uiService.translate( 'genericSearch.nullValueMenuEntry' );
                    this.options.push( { id: 'ISNULL', label: nullValueLabel } );

                    console.log( 'widget.value', this.widget.value );
                    const valueOptions: HisOption[] = this.widget.value;
                    if ( Array.isArray( valueOptions ) ) {
                        valueOptions.filter( opt => opt.id === 'ISNULL' )
                            .forEach( opt => opt.label = nullValueLabel );

                        this.widget.value = [...valueOptions]; // neue Array-Referenz
                    }
                }

                if ( this.widget.kind !== 'multi-dropdown' ) {
                    // the label for the null item should be '', see #210958
                    // the empty selection should always be available on the top of the options list, see #325731
                    // if this.widget.kind === 'multiselect', we don't need the empty selection
                    this.options.push( { id: null, label: '' } );
                }

                this.options = this.options.concat( values );
                console.log( 'Obtained options for widget', this.widget.id, '=', this.options );
            }
        } );
    }

    ngAfterViewInit() {
        // Set focus on first input element
        if ( this.isFirstInputElement ) {
            setTimeout( () => this.inputElement.inputEL.nativeElement.focus() );
        }
    }

    getHtmlFocusElementId(): string {
        return this.uid;
    }

    /**
     * Find dropdown suggestions. The event should only fire if the autocomplete input length >= minLength
     *
     * @param $event event containing the input field value
     */
    filterSelectList( $event: { query: string } ) {
        let autocompleteQuery = $event.query;
        console.log( 'HisSearchMultiselectComponent.filterSelectList(): event =', $event, ', query =', autocompleteQuery );
        // Produce filteredSelectList:
        // * If the new query contains the previous one, then we filter the last filtering result again
        // * If the new query does not contain the last one (because some characters were removed from the
        //   autocomplete input field), then we filter 'options'
        // * Case is ignored.
        console.log(
            'HisSearchMultiselectComponent.filterSelectList(): Old filteredSelectList =',
            this.filteredSelectList, ', lastAutocompleteQuery =', this.lastAutocompleteQuery );
        let srcList = ( this.lastAutocompleteQuery && autocompleteQuery.indexOf( this.lastAutocompleteQuery ) > -1 )
            ? this.filteredSelectList : this.options;
        let nextList: HisOption[] = [];
        let listElem: HisOption;
        for ( listElem of srcList ) {
            if ( listElem.label.toUpperCase().indexOf( autocompleteQuery.toUpperCase() ) > -1 ) {
                nextList.push( listElem );
            }
        }
        this.filteredSelectList = nextList;
        console.log(
            'HisSearchMultiselectComponent.filterSelectList(): Filtered options on client, new filteredSelectList =',
            this.filteredSelectList );

        this.lastAutocompleteQuery = autocompleteQuery;
    }

    /**
     * Add one element selected by autocomplete.
     *
     * @param selectedElement the element to add
     */
    addSelectedElement( event: AutoCompleteSelectEvent ) {
        let selectedElement = event.value as HisOption;
        console.log( 'Element selected by autocomplete:', selectedElement );
        // We do not want to add an element twice; just tell the user that the element is already there.
        if ( this.isElementAlreadySelected( selectedElement.id ) ) {
            let message = this.uiService.translate( 'genericSearch.alreadySelected', selectedElement.label );
            this.notificationService.clear();
            this.notificationService.info( message, 'Info' );
            return;
        }

        // Add selectedElement to selectedOptions:
        // The spread-operator is required to have model and view synchronized
        // (with push() the reset button wouldn't work correctly)
        this.selectedOptions = [...this.selectedOptions, selectedElement];
        console.log( 'emit selectedOptions=', this.selectedOptions );
        this.changed.emit( this.selectedOptions );
    }

    private isElementAlreadySelected( id: string ): boolean {
        // console.log( 'id =', id, 'has type', typeof id );
        for ( let option of this.selectedOptions ) {
            let optionId: string | number = option.id;
            // TODO According to class HisOption the option.id should be of string type
            // But obviously the search result obtained from the REST service contains number ids...
            // console.log( 'optionId =', optionId, 'has type', typeof optionId );
            if ( id === optionId.toString() ) {
                return true;
            }
        }
        return false;
    }

    /**
     * Delete the given option from the list of selected search result rows.
     *
     * @param option the element to remove from the selection
     */
    deleteSelectedOption( option: HisOption ) {
        for ( let i = 0; i < this.selectedOptions.length; i++ ) {
            if ( this.selectedOptions[i] === option ) {
                this.selectedOptions.splice( i, 1 );
                break;
            }
        }
        this.fireOnChange();
    }

    /**
     * The selection has been deleted -> set model value to null.
     */
    onClear() {
        this.selectedOptions = [];
        this.fireOnChange();
    }

    /**
     * Notify HisSearchFormWidgetComponent that the model needs to be updated.
     */
    private fireOnChange() {
        console.log( 'HisSearchMultiselectComponent: Emit change event:', this.selectedOptions );
        this.changed.emit( this.selectedOptions );
    }
}
